import type { ApiErrorBody } from "./types";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export interface ApiClientOptions {
  baseUrl: string;
  /** Returns the current JWT or null. */
  getToken: () => string | null;
  /** Stable per-install id sent as X-Device-Id (hashed server-side). */
  deviceId: string;
  /** Called on 401 so the app can re-authenticate. */
  onUnauthorized?: () => void;
  fetch?: typeof fetch;
}

export interface ApiClient {
  get<T>(path: string): Promise<T>;
  post<T>(path: string, body?: unknown): Promise<T>;
}

export function createApiClient(opts: ApiClientOptions): ApiClient {
  const fetchImpl = opts.fetch ?? fetch;
  const base = opts.baseUrl.replace(/\/$/, "");

  async function request<T>(method: "GET" | "POST", path: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = {
      accept: "application/json",
      "x-device-id": opts.deviceId,
    };
    const token = opts.getToken();
    if (token) headers["authorization"] = `Bearer ${token}`;
    if (body !== undefined) headers["content-type"] = "application/json";

    const init: RequestInit = { method, headers };
    if (body !== undefined) init.body = JSON.stringify(body);
    const res = await fetchImpl(`${base}${path}`, init);

    if (res.status === 204) return undefined as T;
    const text = await res.text();
    let json: unknown = null;
    try {
      json = text ? JSON.parse(text) : null;
    } catch {
      /* non-JSON body */
    }

    if (!res.ok) {
      const err = (json as ApiErrorBody | null)?.error;
      if (res.status === 401) opts.onUnauthorized?.();
      const { code, message, ...details } = err ?? {
        code: "HTTP_ERROR",
        message: text || res.statusText,
      };
      throw new ApiError(res.status, code, message, details);
    }
    return json as T;
  }

  return {
    get: (path) => request("GET", path),
    post: (path, body) => request("POST", path, body),
  };
}
